import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Row } from "reactstrap";
import Select from "react-select";
import { Tooltip } from "@mui/material";
import { FloppyDisk, Plus } from "@phosphor-icons/react";
import indicadoresService from "services/IndicadoresService";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";

const customStyles = {
	option: (provided, state) => ({
		...provided,
		color: state.isSelected ? "blue" : "black",
		backgroundColor: "white",
		"&:hover": { backgroundColor: "#556ee6" },
	}),
};

const quantifierOptions = [
	{ value: "R$", label: "R$ (Reais)" },
	{ value: "%", label: "% (Percentual)" },
	{ value: "x", label: "x (Vezes)" },
	{ value: "h", label: "h (Horas)" },
	{ value: "Un", label: "Un (Unidades)" },
	{ value: "pts", label: "pts (Pontos)" },
];

const COLORS = [
	"#22c55e", "#3b82f6", "#f97316", "#06b6d4", "#eab308",
	"#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f59e0b",
	"#6366f1", "#10b981",
];

const OPERATORS = [
	{ value: "+", label: "+" },
	{ value: "-", label: "-" },
	{ value: "*", label: "×" },
	{ value: "/", label: "÷" },
	{ value: "(", label: "(" },
	{ value: ")", label: ")" },
];

const NUMBER_KEYS = ["7", "8", "9", "4", "5", "6", "1", "2", "3", "0", ","];

const getRandomColor = () => COLORS[Math.floor(Math.random() * COLORS.length)];

const getFallbackColorForConfig = (config) => {
	const key = String(config?.id ?? "");
	let hash = 0;
	for (let i = 0; i < key.length; i++) {
		hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
	}
	return COLORS[hash % COLORS.length];
};

const getConfigName = (config) =>
	config?.GoalsVariable?.name || config?.name || "";

const tokenizeFormula = (formula, variableNames = []) => {
	if (!formula) return [];
	const names = [...variableNames]
		.filter(Boolean)
		.sort((a, b) => b.length - a.length);
	const tokens = [];
	let i = 0;
	const s = String(formula);

	while (i < s.length) {
		const ch = s[i];
		if ("+-*/()".includes(ch)) {
			tokens.push(ch);
			i += 1;
			continue;
		}
		if (/\d/.test(ch) || ch === "." || ch === ",") {
			let j = i + 1;
			while (j < s.length && /[\d.,]/.test(s[j])) j += 1;
			// Exibe decimal no padrão pt-BR
			tokens.push(s.slice(i, j).replace(/\./g, ","));
			i = j;
			continue;
		}
		if (/\s/.test(ch)) {
			i += 1;
			continue;
		}

		let matched = false;
		for (const name of names) {
			if (s.startsWith(name, i)) {
				tokens.push(name);
				i += name.length;
				matched = true;
				break;
			}
		}
		if (!matched) {
			tokens.push(ch);
			i += 1;
		}
	}

	return tokens;
};

const formulaHelpTooltip = (
	<div style={{ fontSize: 12, lineHeight: 1.6, padding: "2px 0" }}>
		<div style={{ fontWeight: 600, marginBottom: 6 }}>Como criar fórmulas</div>
		<div>• Clique nas variáveis à esquerda para inserir na fórmula</div>
		<div>
			• Use os operadores: + soma, − subtração, × multiplicação, ÷ divisão
		</div>
		<div>• Exemplo: VENDA+CUSTO</div>
		<div>• Exemplo: (LUCRO/RECEITA)*100</div>
	</div>
);

const builderButtonStyle = (active = false) => ({
	minWidth: 44,
	height: 40,
	border: "1px solid #dee2e6",
	borderRadius: 6,
	backgroundColor: active ? "#212529" : "#fff",
	color: active ? "#fff" : "#212529",
	fontWeight: 600,
	fontSize: 15,
	cursor: "pointer",
	padding: "0 10px",
});

const CalculatedVariableModal = ({
	isOpen,
	onClose,
	goalId,
	availableVariables,
	editingConfig,
	onVariableCreated,
	onVariableUpdated,
}) => {
	const isEditMode = !!editingConfig;
	const [name, setName] = useState("");
	const [quantifier, setQuantifier] = useState(null);
	const [tokens, setTokens] = useState([]);
	const [saving, setSaving] = useState(false);

	const variablesForFormula = useMemo(
		() =>
			(availableVariables || []).filter(
				(v) => !editingConfig || String(v.id) !== String(editingConfig.id),
			),
		[availableVariables, editingConfig],
	);

	useEffect(() => {
		if (!isOpen) return;

		if (editingConfig) {
			setName(editingConfig.name || "");
			setQuantifier(
				quantifierOptions.find(
					(opt) => opt.value === (editingConfig.quantifier || ""),
				) || null,
			);
			const otherNames = (availableVariables || [])
				.filter((v) => String(v.id) !== String(editingConfig.id))
				.map((v) => getConfigName(v))
				.filter(Boolean);
			setTokens(tokenizeFormula(editingConfig.formula || "", otherNames));
		} else {
			setName("");
			setQuantifier(null);
			setTokens([]);
		}
	}, [isOpen, editingConfig, availableVariables]);

	const formula = tokens.join("");
	// Persistimos com ponto para o motor de cálculo (JS) aceitar o número
	const formulaToPersist = formula.replace(/,/g, ".");

	const appendToken = (token) => {
		if (!token) return;
		setTokens((prev) => [...prev, token]);
	};

	const handleBackspace = () => {
		setTokens((prev) => prev.slice(0, -1));
	};

	const handleClear = () => {
		setTokens([]);
	};

	const handleNumberKey = (key) => {
		appendToken(key);
	};

	const handleSave = async () => {
		if (!name.trim()) {
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Atenção",
				text: "Informe o nome da variável calculada.",
				customClass: { confirmButton: "custom-confirm-button" },
			});
			return;
		}
		if (!quantifier) {
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Atenção",
				text: "Selecione o quantificador.",
				customClass: { confirmButton: "custom-confirm-button" },
			});
			return;
		}
		if (!formulaToPersist.trim()) {
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Atenção",
				text: "Monte a fórmula usando as variáveis e os operadores.",
				customClass: { confirmButton: "custom-confirm-button" },
			});
			return;
		}

		setSaving(true);
		try {
			if (isEditMode) {
				onVariableUpdated?.(editingConfig.id, {
					name: name.trim(),
					quantifier: quantifier.value,
					formula: formulaToPersist.trim(),
				});
				handleClose();
				return;
			}

			const nextSequence = (availableVariables.length || 0) + 1;
			const randomColor = getRandomColor();

			if (!goalId) {
				const localConfig = {
					id: "temp_calc_" + Date.now(),
					goals_variables_id: null,
					name: name.trim(),
					quantifier: quantifier.value,
					type: "2",
					formula: formulaToPersist.trim(),
					display_on_chart: 1,
					sequence: nextSequence,
					color: randomColor,
					totalizator: null,
					totalizator_ref: null,
				};
				onVariableCreated(localConfig);
				handleClose();
			} else {
				const result = await indicadoresService.addVariableConfig({
					goals_id: goalId,
					goals_variables_id: null,
					name: name.trim(),
					quantifier: quantifier.value,
					type: "2",
					formula: formulaToPersist.trim(),
					display_on_chart: 1,
					sequence: nextSequence,
					color: randomColor,
				});
				onVariableCreated(result);
				handleClose();
			}
		} catch (error) {
			console.error(
				isEditMode
					? "Erro ao atualizar variável calculada:"
					: "Erro ao criar variável calculada:",
				error,
			);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: isEditMode
					? "Não foi possível atualizar a variável calculada."
					: "Não foi possível criar a variável calculada.",
				customClass: { confirmButton: "custom-confirm-button" },
			});
		} finally {
			setSaving(false);
		}
	};

	const handleClose = () => {
		setName("");
		setQuantifier(null);
		setTokens([]);
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			toggle={handleClose}
			size="lg"
			centered
			style={{ minWidth: "860px" }}
		>
			<div
				className="modal-header"
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
				}}
			>
				<div>
					<h5 style={{ marginBottom: 4 }}>
						{isEditMode
							? `Editar Variável Calculada: ${editingConfig?.name || ""}`
							: "Criar Variável Calculada"}
					</h5>
					<div style={{ fontSize: 13, color: "#6c757d" }}>
						Monte a fórmula clicando nas variáveis, operadores e números.
						A digitação manual fica bloqueada para evitar erros.
					</div>
				</div>
				<button
					type="button"
					className="btn-close"
					onClick={handleClose}
					aria-label="Close"
				/>
			</div>
			<ModalBody style={{ padding: "20px 24px" }}>
				<Row className="mb-3">
					<Col md={7}>
						<FormGroup className="mb-0">
							<Label>
								Nome da Variável Calculada{" "}
								<span className="text-danger">*</span>
							</Label>
							<Input
								type="text"
								placeholder="Ex: VENDA TOTAL"
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</FormGroup>
					</Col>
					<Col md={5}>
						<FormGroup className="mb-0">
							<Label>
								Quantificador <span className="text-danger">*</span>
							</Label>
							<Select
								styles={customStyles}
								placeholder="Selecione"
								value={quantifier}
								onChange={setQuantifier}
								options={quantifierOptions}
								menuPortalTarget={document.body}
								menuPosition="fixed"
							/>
						</FormGroup>
					</Col>
				</Row>

				<FormGroup className="mb-3">
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 6,
						}}
					>
						<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
							<Label className="mb-0">
								Fórmula <span className="text-danger">*</span>
							</Label>
							<Tooltip
								title={formulaHelpTooltip}
								placement="top"
								arrow
								componentsProps={{
									tooltip: {
										sx: {
											bgcolor: "#212529",
											maxWidth: 320,
											fontSize: 12,
										},
									},
								}}
							>
								<span
									style={{
										fontSize: 12,
										color: "#556ee6",
										cursor: "help",
										textDecoration: "underline",
										textUnderlineOffset: 2,
									}}
								>
									Como criar fórmulas
								</span>
							</Tooltip>
						</div>
						<div style={{ display: "flex", gap: 6 }}>
							<button
								type="button"
								onClick={handleBackspace}
								disabled={tokens.length === 0}
								style={{
									...builderButtonStyle(),
									minWidth: 64,
									height: 32,
									fontSize: 13,
									opacity: tokens.length === 0 ? 0.5 : 1,
								}}
								title="Apagar último"
							>
								⌫
							</button>
							<button
								type="button"
								onClick={handleClear}
								disabled={tokens.length === 0}
								style={{
									...builderButtonStyle(),
									minWidth: 64,
									height: 32,
									fontSize: 13,
									opacity: tokens.length === 0 ? 0.5 : 1,
								}}
								title="Limpar fórmula"
							>
								Limpar
							</button>
						</div>
					</div>
					<div
						style={{
							minHeight: 52,
							padding: "12px 14px",
							backgroundColor: "#f8f9fa",
							border: "1px solid #dee2e6",
							borderRadius: 8,
							fontFamily: "monospace",
							fontSize: 14,
							wordBreak: "break-word",
							color: formula ? "#212529" : "#adb5bd",
						}}
					>
						{formula || "Clique nos botões abaixo para montar a fórmula"}
					</div>
				</FormGroup>

				<Row>
					<Col md={7}>
						<div
							style={{
								border: "1px solid #dee2e6",
								borderRadius: 8,
								padding: 12,
								height: "100%",
								minHeight: 260,
							}}
						>
							<div style={{ fontSize: 12, color: "#6c757d", marginBottom: 8 }}>
								Variáveis deste indicador
							</div>
							{variablesForFormula.length === 0 ? (
								<div style={{ fontSize: 12, color: "#adb5bd" }}>
									Nenhuma variável adicionada ainda.
								</div>
							) : (
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										gap: 4,
										maxHeight: 280,
										overflowY: "auto",
									}}
								>
									{variablesForFormula.map((v) => {
										const varName = getConfigName(v) || "—";
										const varUnit =
											v.GoalsVariable?.quantifier ||
											v.quantifier ||
											"";
										const varColor =
											v.color || getFallbackColorForConfig(v);
										return (
											<button
												key={v.id}
												type="button"
												onClick={() => appendToken(varName)}
												style={{
													display: "flex",
													alignItems: "center",
													gap: 8,
													padding: "8px 10px",
													border: "1px solid #e9ecef",
													background: "#fff",
													cursor: "pointer",
													borderRadius: 6,
													width: "100%",
													textAlign: "left",
												}}
												onMouseEnter={(e) =>
													(e.currentTarget.style.backgroundColor =
														"#f8f9fa")
												}
												onMouseLeave={(e) =>
													(e.currentTarget.style.backgroundColor =
														"#fff")
												}
											>
												<span
													style={{
														width: 8,
														height: 8,
														borderRadius: 2,
														backgroundColor: varColor,
														flexShrink: 0,
													}}
												/>
												<span
													style={{
														fontSize: 13,
														fontWeight: 600,
														flex: 1,
													}}
												>
													{varName}
												</span>
												{varUnit && (
													<span
														style={{
															fontSize: 12,
															color: "#6c757d",
														}}
													>
														{varUnit}
													</span>
												)}
											</button>
										);
									})}
								</div>
							)}
						</div>
					</Col>

					<Col md={5}>
						<div style={{ marginBottom: 10, fontSize: 12, color: "#6c757d" }}>
							Operadores
						</div>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
								gap: 8,
								marginBottom: 16,
							}}
						>
							{OPERATORS.map((op) => (
								<button
									key={op.value}
									type="button"
									onClick={() => appendToken(op.value)}
									style={builderButtonStyle()}
								>
									{op.label}
								</button>
							))}
						</div>

						<div style={{ marginBottom: 10, fontSize: 12, color: "#6c757d" }}>
							Números
						</div>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
								gap: 8,
							}}
						>
							{NUMBER_KEYS.map((key) => (
								<button
									key={key}
									type="button"
									onClick={() => handleNumberKey(key)}
									style={{
										...builderButtonStyle(),
										gridColumn: key === "0" ? "span 2" : undefined,
									}}
								>
									{key}
								</button>
							))}
						</div>
					</Col>
				</Row>
			</ModalBody>
			<ModalFooter>
				<Button
					type="button"
					style={{ background: "none", padding: 10, color: "black" }}
					onClick={handleClose}
				>
					Cancelar
				</Button>
				<Button
					type="button"
					disabled={saving}
					onClick={handleSave}
					style={{
						background: "none",
						color: "black",
						display: "flex",
						alignItems: "center",
						gap: 4,
						padding: 10,
					}}
				>
					{saving ? (
						<span
							className="spinner-border spinner-border-sm"
							style={{ marginRight: 6 }}
						/>
					) : isEditMode ? (
						<FloppyDisk size={16} />
					) : (
						<Plus size={16} />
					)}
					{isEditMode ? "Salvar" : "Criar e Adicionar ao Indicador"}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default CalculatedVariableModal;
